import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useBetterAuthConvexAuth } from '../hooks/useAppAuth';
import { authClient } from '../lib/auth-client';
import InitialSetup from '../components/Auth/InitialSetup';

const SIGN_IN_TIMEOUT_MS = 20000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('Sign in timed out. Please try again.'));
    }, timeoutMs);

    promise
      .then((value) => {
        window.clearTimeout(timer);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timer);
        reject(error);
      });
  });
}

const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { convexUser, isAdmin, isSignedIn, isLoaded } = useBetterAuthConvexAuth();
  const hasAdmin = useQuery(api.users.hasAdmin);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isSignedIn && convexUser) {
      if (isAdmin) {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isSignedIn, convexUser, isAdmin, navigate]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (hasAdmin === false) {
    return <InitialSetup onComplete={() => window.location.reload()} />;
  }

  if (hasAdmin === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const result = await withTimeout(
        authClient.signIn.email({
          email,
          password,
        }),
        SIGN_IN_TIMEOUT_MS,
      );

      if (result.error) {
        setError(result.error.message || 'Invalid admin email or password');
        setIsSubmitting(false);
        return;
      }

      // Redirect fallback in case auth state propagation is delayed.
      setIsSubmitting(false);
      window.location.assign('/admin');
    } catch (err: any) {
      setError(err.message || 'Sign in failed. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-white p-4 flex justify-between items-center">
        <div className="flex items-center">
          <img
            src="/ASG_Maintenance_side.png"
            alt="A Shade Greener Maintenance"
            className="h-14 w-auto"
          />
        </div>
        <a href="https://www.ashadegreener.co.uk" className="text-sm text-gray-600 hover:text-gray-900">
          Return to main site
        </a>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          <h1 className="text-center text-4xl md:text-5xl font-semibold tracking-tight leading-[1.15] text-gray-900 mb-10">
            Admin portal
            <span className="block mt-1 text-green-700">Sign in</span>
          </h1>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm text-center">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Admin Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your admin email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gray-800 hover:bg-gray-700 disabled:bg-gray-400 text-white font-medium py-3 px-6 rounded-md transition-colors duration-200"
            >
              {isSubmitting ? 'Signing in...' : 'Sign in to Admin'}
            </button>
          </form>

          {/* <p className="mt-6 text-center text-sm text-gray-600">
            <Link to="/login" className="text-shade-green-600 hover:text-shade-green-700">
              Back to main site
            </Link>
            {' '}|{' '}
            <Link to="/login" className="text-shade-green-600 hover:text-shade-green-700">
              Customer Login
            </Link>
          </p> */}
        </div>
      </main>

      <footer className="bg-gray-800 text-white text-center py-4 text-sm">
        <p className="opacity-60">
          Sterling House, Maple Court, Tankersley, S75 3DP | Privacy Policy
        </p>
      </footer>
    </div>
  );
};

export default AdminLoginPage;
